import React, { useEffect, useState, lazy, Suspense, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useEntireData, usePostMessageParams, useDevelopment, usePluginTests, useMockTests } from '../../common-effects';
import { useMediaQuery } from 'react-responsive';
import { isMobile } from 'react-device-detect';
import cn from 'classnames';

import styles from './app-container.module.scss';
import Preloader from '../preloader/preloader';
import ServerError from '../server-error/server-error';
import InitialView from '../initial-view/initial-view';
import Footer from '../footer/footer';
import CloseButton from '../close-button/close-button';
import Label from '../label/label';
import Header from '../header/header';

import {
	EVENT_LOAD_QUIZ,
	EVENT_CLOSE_QUIZ,
	EVENT_CLICK_TRADEMARK,
	EVENT_SHOW_FLYING_BTN,
	EVENT_SHOW_PHONE_FORM_VISUAL,
	EVENT_GO_TO_SERVICE_SELECTION,
	SEND_PARAMS,
	EVENT_CUSTOM_CONTENT_ADD
} from '../../constants';
import { emitEvent, quizEventEmitter } from '../../event-emitter';
import { RootState } from '../../store';
import { StateType } from '../../reducers/history/enums/state-type.enum';
import { moveTo, back, animConfig } from '../../state-switcher';
import { componentLoader } from '../component-loader/component-loader';

import Progress from '../progress/progress';
import Doctor from '../doctor/doctor';
import ButtonBack from '../button-back/button-back';
import ErrorMessage from '../error-message/error-message';
import ButtonToCommon from '../button-to-common/button-to-common';
import Offer from '../offer/offer';
import { scrollToTop } from '../../utils/library';
import { ConvrTestName } from '../../enums/convr-test-name.enum';
import { Service } from '../../models/service.model';
import { FastForm } from '../fast-form/fast-form';
import { PDNNotification } from '../pdn-notification/pdn-notification';
import { usePolicyAgree } from '../../hooks/use-policy-agree.hook';

const QuestionContainer = lazy(() => componentLoader(() => import('../question-container/question-container')));
const Application = lazy(() => componentLoader(() => import('../application/application')));
const PreFinal = lazy(() => componentLoader(() => import('../pre-final/pre-final')));
const Final = lazy(() => componentLoader(() => import('../final/final')));
const SignUp = lazy(() => componentLoader(() => import('../sign-up/sign-up')));
const ServiceSelection = lazy(() => componentLoader(() => import('../service-selection/service-selection')));
const InfoBlock = lazy(() => componentLoader(() => import('../info-block/info-block')));
const MobileBonuses = lazy(() => componentLoader(() => import('../mobile-bonuses/mobile-bonuses')));


const initialViewContent = {
	offer: undefined,
	description: `Мы предоставим вам предварительный расчет стоимости и варианты 
	\nлечения на основе ваших ответов. Также вы получите возможность 
	\nрассрочки. Нас выбрали уже тысячи пациентов`
};

function AppContainer(props: ReturnType<typeof mapStateToProps>) {
	usePostMessageParams();
	// useDevelopment("whitening");
	useEntireData();
	useEffect(() => emitEvent(EVENT_LOAD_QUIZ), []);
	// const testVariations = useMockTests();
	const testVariations = usePluginTests();
	// useAbTest();

	const { quiz, main, history, client, phoneForm } = props;
	const { questions = [], expertViewStatus, currentService, buttonBackViewStatus,
		flyingButtonStatus, hasPhone, quizProgress, serviceListDropdownMenuStatus } = quiz;
	const { progressBar, preloader, serverError, error, initialPageIndex } = main;
	const { currentState } = history;
	const { specialOffers, bonusesStatusFromPlugin, gotEntireData, gotCustomContent, hideFlyingButton } = client || {};

	const enableSpecialOffers = useMemo(() => {
		return (bonusesStatusFromPlugin ?? specialOffers?.enable) && Boolean(specialOffers?.list?.length);
	}, [bonusesStatusFromPlugin, specialOffers]);

	const mobile700 = useMediaQuery({ maxWidth: 700 });

	const divided = enableSpecialOffers && !isMobile && !mobile700;
	const showDivision = divided && (currentState.type == StateType.QUESTIONS ||
		currentState.type == StateType.FORM ||
		currentState.type == StateType.RESULT);

	const wasOpened = useMemo(() => main.wasOpened, [main]);

	useEffect(() => {
		const subscription = quizEventEmitter.subscribe(EVENT_CUSTOM_CONTENT_ADD, () => {
			emitEvent(SEND_PARAMS, {
				wa_enabled: Boolean(client?.whatsApp?.enable && isMobile),
				wa_first_page: Boolean(client?.whatsApp?.enable && client?.whatsApp?.onFirstScreen && isMobile),
				wa_doctor: Boolean(client?.whatsApp?.enable && client?.whatsApp?.inDoctorBlock && isMobile)
			});
		})

		subscription.unsubscribeOnEmit();

		return () => subscription.unsubscribe();
	}, [client]);


	const flyingButtonVisible = useMemo(() => {
		if (!gotEntireData || !gotCustomContent || isMobile || initialPageIndex != 0 || hideFlyingButton || !wasOpened) return false;
		return true;
	}, [gotEntireData, gotCustomContent, hideFlyingButton, initialPageIndex, wasOpened]);

	useEffect(() => {
		if (flyingButtonVisible) {
			emitEvent(EVENT_SHOW_FLYING_BTN);
		}
	}, [flyingButtonVisible]);

	useEffect(() => {
		if (client?.application?.visual) {
			emitEvent(EVENT_SHOW_PHONE_FORM_VISUAL);
		}
	}, [client]);

	function goToSelection() {
		if (client?.showCommonServiceList) {
			moveTo({ type: StateType.SELECTION }, scrollToTop);
			emitEvent(EVENT_GO_TO_SERVICE_SELECTION);
		}
	}

	const closeHandler = () => emitEvent(EVENT_CLOSE_QUIZ);
	const labelClickHandler = () => emitEvent(EVENT_CLICK_TRADEMARK);

	const LabelElem = <Label
		domain={client?.domain}
		{...client?.customLabel}
		modeAlone={currentState?.type != StateType.INITIAL && currentState?.type != StateType.RESULT}
		onClick={labelClickHandler}
	/>

	const [serviceList, setServiceList] = useState(client?.serviceList);
	const [serviceColumns, setServiceColumns] = useState(1);
	const [serviceImages, setSerivceImages] = useState("DEFAULT");
	const [serviceIcons, setServiceIcons] = useState("DEFAULT");
	const [serviceWayToShow, setServiceWayToShow] = useState("DEFAULT");
	const [buttonType, setButtonType] = useState({
		form: "DEFAULT",
		size: "DEFAULT",
		animation: "DEFAULT"
	});
	const [phoneInputType, setPhoneInputType] = useState({
		padding: "DEFAULT",
		border: "DEFAULT",
		font_size: "DEFAULT",
		placeholder: "DEFAULT"
	});
	const [nextButtonMode, setNextButtonMode] = useState("DEFAULT");

	useEffect(() => {
		setServiceList(client?.serviceList);

		if (testVariations?.length) {
			const serviceCountFromTest = testVariations.find(variation => variation.name == ConvrTestName.SERVICE_COUNT)?.variant?.payload;
			const serviceColumnsFromTest = testVariations.find(variation => variation.name == ConvrTestName.SERVICE_COLUMNS)?.variant?.payload;
			const serviceImagesFromTest = testVariations.find(variation => variation.name == ConvrTestName.SERVICE_BACKGROUND)?.variant?.payload;
			const serviceIconsFromTest = testVariations.find(variation => variation.name == ConvrTestName.SERVICE_ICONS)?.variant?.payload;
			const serviceOrderFormTest = testVariations.find(variation => variation.name == ConvrTestName.SERVICE_ORDER)?.variant?.payload;
			const serviceWayToShowFromTest = testVariations.find(variation => variation.name == ConvrTestName.WAY_TO_SHOW_SERVICES)?.variant?.payload;
			/*----------------*/
			const buttonFormFromTest = testVariations.find(variation => variation.name == ConvrTestName.BUTTON_FORM)?.variant?.payload;
			const buttonAnimationFromTest = testVariations.find(variation => variation.name == ConvrTestName.BUTTON_ANIMATION)?.variant?.payload;
			/*----------------*/
			const phoneInputPaddingFromTest = testVariations.find(variation => variation.name == ConvrTestName.PHONE_INPUT_PADDING)?.variant?.payload;
			const phoneInputBorderFromTest = testVariations.find(variation => variation.name == ConvrTestName.PHONE_INPUT_BORDER)?.variant?.payload;
			const phoneInputFontSizeFromTest = testVariations.find(variation => variation.name == ConvrTestName.PHONE_INPUT_FONT_SIZE)?.variant?.payload;
			/*----------------*/
			const nextButtonModeFromTest = testVariations.find(variation => variation.name == ConvrTestName.NEXT_BUTTON)?.variant?.payload;

			if (serviceWayToShowFromTest !== undefined) {
				setServiceWayToShow(serviceWayToShowFromTest);
			}

			let newList: Service[] = [];
			if (serviceOrderFormTest !== undefined && client?.serviceList && client?.serviceList?.length > 1) {

				if (serviceOrderFormTest != "DEFAULT") {
					newList = serviceOrderFormTest;
				} else {
					newList = [...client.serviceList];
				}
			}

			if (serviceCountFromTest !== "DEFAULT" && serviceCountFromTest && typeof Number(serviceCountFromTest) == "number" && client?.serviceList && client?.serviceList?.length > 1) {
				const allServices: Service[] = ['implantation', 'prosthetics', 'treatment', 'braces', 'cleaning', 'whitening', 'veneers', 'pediatrics', 'crowns', 'gums', 'remove', 'rem_wisdom'];
				allServices.forEach(service => {
					if (!newList.includes(service)) newList.push(service);
				});
				newList.length = serviceCountFromTest;
				setServiceList(newList);
			}

			if (serviceColumnsFromTest !== undefined) setServiceColumns(serviceColumnsFromTest);
			if (serviceImagesFromTest !== undefined) setSerivceImages(serviceImagesFromTest);
			if (serviceIconsFromTest !== undefined) setServiceIcons(serviceIconsFromTest);
			if (buttonFormFromTest !== undefined || buttonAnimationFromTest !== undefined) {
				setButtonType(prevData => ({
					form: buttonFormFromTest || prevData.form,
					size: prevData.size,
					animation: buttonAnimationFromTest || prevData.animation
				}));
			}

			if (phoneInputPaddingFromTest !== undefined || phoneInputBorderFromTest !== undefined || phoneInputFontSizeFromTest !== undefined) {
				setPhoneInputType(prevData => ({
					padding: phoneInputPaddingFromTest || prevData.padding,
					border: phoneInputBorderFromTest || prevData.border,
					font_size: phoneInputFontSizeFromTest || prevData.font_size,
					placeholder: prevData.placeholder
				}));
			}

			if (nextButtonModeFromTest !== "DEFAULT") {
				setNextButtonMode(nextButtonModeFromTest);
			}

		}
	}, [client, testVariations]);

	const { policyStatus, setPolicyAgree } = usePolicyAgree();

	return (<div className={cn(styles.app, { [styles.hasError]: error, [styles.divided]: divided })}>
		<Preloader {...preloader} />
		{client?.scenario == 'fullPage' && !policyStatus && <PDNNotification
			mode={client.policyAgreementMode}
			policyLink={client.privacyPolicy || ""}
			onClose={setPolicyAgree}
			text={client.policyAgreementNotificationText}
		/>}

		{serverError && <>
			{Number(serverError) < 500 ?
				<ServerError code={Number(serverError)} />
				:
				<FastForm
					currentService={currentService}
					whatsApp={client?.whatsApp}
					phoneFormState={phoneForm}
					moveTo={moveTo}
				/>
			}
		</>}

		{client && <>
			{(enableSpecialOffers && isMobile) &&
				<Suspense fallback={<div></div>}>
					<MobileBonuses
						bonuses={specialOffers?.list || []}
						quizProgress={quizProgress}
						active={currentState.type == StateType.QUESTIONS}
						headerReady={client.bonusHeaderReady}
						headerInProcess={client.bonusHeaderInProcess}
					/>
				</Suspense>}

			{mobile700 && <ErrorMessage error={error} />}

			{!showDivision && <>
				<Progress
					quizProgress={quizProgress}
					active={progressBar.active}
					currentState={currentState}
					total={questions.length + 1}
				/>
				<Doctor {...client.doctor} active={expertViewStatus} whatsApp={client.whatsApp} />
				<ButtonBack active={buttonBackViewStatus} onClick={back} />
			</>}

			{showDivision &&
				<Suspense fallback={<div style={{ width: '23%' }} />}>
					<InfoBlock
						expert={client.doctor}
						active={expertViewStatus}
						headerReady={client.bonusHeaderReady}
						headerInProcess={client.bonusHeaderInProcess}
						bonuses={client?.specialOffers?.list}
						quizProgress={quizProgress}
						unlocked={hasPhone}
					/>
				</Suspense>
			}

			{((client.scenario != 'fullPage' || client.closeButton?.show) && client.scenario != 'custom') && <CloseButton onClick={closeHandler} />}

			<main className={cn(styles.default, { [styles.mainFlexBlock]: showDivision })}>

				{showDivision && <>
					<Progress quizProgress={quizProgress} active={progressBar.active} currentState={currentState} total={questions.length + 1} />
					<ButtonBack mode="division" active={buttonBackViewStatus} onClick={back} />
				</>}

				{currentState.type == StateType.INITIAL &&
					<InitialView
						animation={animConfig}
						serviceList={client.serviceList || []}
						currentService={currentService}
						description={client.initialViewContent?.description || initialViewContent.description}
						next={moveTo}
						flyingButtonEnabled={flyingButtonVisible}
						flyingButtonStatus={flyingButtonStatus}
						additionalCallbackButton={client.additionalCallbackButton}
						hasHeader={Boolean(client.header)}
						canStartOnScroll={!serviceListDropdownMenuStatus}
						wasOpened={wasOpened}
						whatsApp={client.whatsApp}
						initialPageIndex={initialPageIndex}
						testVariations={testVariations}
						buttonType={buttonType}
						backgroundImage={client.backgroundImage}
						phoneFormState={phoneForm}
						autoAction={client.startPageAutoAction}
					>
						{client.header && <Header active={animConfig.active} data={client.header}>
							{(client.showCommonServiceList && client.commonServiceList && client.serviceList?.length == 1) &&
								<ButtonToCommon onClick={goToSelection} />}
						</Header>}
						<Offer
							text={client.initialViewContent?.offer || initialViewContent.offer}
							serviceList={serviceList}
							currentService={currentService}
							clickOnSelectServiceBtn={goToSelection}
							commonServiceList={client.showCommonServiceList ? client.commonServiceList : undefined}
							animation={animConfig}
						/>

					</InitialView>
				}

				{currentState.type == StateType.QUESTIONS && questions.length &&
					<Suspense fallback={<Preloader active={true} />}>
						<QuestionContainer
							animation={animConfig}
							currentState={currentState}
							questions={questions}
							next={moveTo}
							error={error}
							labelElem={LabelElem}
							scenario={client.scenario}
							divided={divided}
							bonuses={client.specialOffers?.list}
							quizProgress={quizProgress}
							buttonType={buttonType}
							nextButtonMode={nextButtonMode}
						/>
					</Suspense>
				}

				{currentState.type == StateType.FORM &&
					<Suspense fallback={<Preloader active={true} />}>
						<Application {...client.application}
							next={moveTo}
							animation={animConfig}
							whatsApp={client.whatsApp}
							service={currentService}
							testVariations={testVariations}
							phoneInputType={phoneInputType}
							phoneFormState={phoneForm}
							specialOffers={{
								enable: enableSpecialOffers,
								list: client.specialOffers?.list
							}}
							buttonType={buttonType}
							additionalAgreement={client.application?.additionalAgreement}
							customContent={client.customContent?.application}
						/>
					</Suspense>
				}

				{currentState.type == StateType.SELECTION &&
					<Suspense fallback={<Preloader active={true} />}>
						<ServiceSelection
							next={moveTo}
							list={(client.serviceList && client.serviceList.length > 1) ? serviceList : client.commonServiceList}
							animation={animConfig}
							testVariations={testVariations}
							columns={serviceColumns}
							imagesVariation={serviceImages}
							serviceIcons={serviceIcons}
							wayToShow={serviceWayToShow}
						/>
					</Suspense>
				}

				{currentState.type == StateType.RESULT &&
					<Suspense fallback={<Preloader active={true} />}>
						<PreFinal
							animation={animConfig}
							next={moveTo}
							buttonType={buttonType}
							header={client.customContent?.preFinalPage?.header}
							subHeader={client.customContent?.preFinalPage?.subHeader}
						/>
					</Suspense>
				}

				{currentState.type == StateType.FINAL &&
					<Suspense fallback={<Preloader active={true} />}>
						<Final
							animation={animConfig}
							moreInfo={client.moreInfo}
							onClose={closeHandler}
							from={currentState.data?.from}
							buttonType={buttonType}
						/>
					</Suspense>
				}

				{currentState.type == StateType.SIGN_UP &&
					<Suspense fallback={<Preloader active={true} />}>
						<SignUp
							animation={animConfig}
							nextStep={moveTo}
							placeholder={client.application?.placeholder}
							currentService={currentService}
							phoneInputType={phoneInputType}
							buttonType={buttonType}
							phoneFormState={phoneForm}
							nameField={client.application?.nameField}
							additionalAgreement={client.application?.additionalAgreement}
						/>
					</Suspense>
				}

				{currentState && <Footer
					license={client.license}
					currentState={currentState.type}
				>
					{!client.whiteLabel && <Label
						domain={client.domain}
						{...client.customLabel}
						modeAlone={currentState.type != StateType.INITIAL && currentState.type != StateType.RESULT}
						onClick={labelClickHandler}
					/>}
				</Footer>}
			</main>
		</>}
	</div>);
}

const mapStateToProps = (state: RootState) => ({
	quiz: state.quiz,
	client: state.client,
	main: state.main,
	history: state.history,
	phoneForm: state.phoneForm
});

export default connect(mapStateToProps)(AppContainer);